// src/services/agentService.js
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Create axios instance with auth headers
const createAuthenticatedRequest = () => {
  // ✅ FIXED: Check both possible token keys (token and authToken)
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  
  if (!token) {
    console.error('❌ No authentication token found');
    throw new Error('No authentication token found. Please login again.');
  }
  
  console.log('🔑 Using token:', token.substring(0, 20) + '...');
  
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    // ✅ ADDED: Request interceptor for debugging
    interceptors: {
      request: [
        (config) => {
          console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`);
          return config;
        },
        (error) => {
          console.error('❌ Request interceptor error:', error);
          return Promise.reject(error);
        }
      ],
      response: [
        (response) => {
          console.log(`✅ API Response: ${response.status} ${response.config.url}`);
          return response;
        },
        (error) => {
          console.error('❌ Response interceptor error:', error.response?.status, error.response?.data);
          
          // ✅ ADDED: Handle 401 errors globally
          if (error.response?.status === 401) {
            console.error('🔒 Authentication failed - redirecting to login');
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            // You might want to redirect to login page here
            window.location.href = '/login';
          }
          
          return Promise.reject(error);
        }
      ]
    }
  });
};

// ✅ ADDED: Helper function to handle API errors consistently
const handleApiError = (error, operation) => {
  console.error(`❌ ${operation} failed:`, error);
  
  if (error.response?.status === 401) {
    throw new Error('Authentication failed. Please login again.');
  } else if (error.response?.status === 403) {
    throw new Error('Access denied. You do not have permission for this operation.');
  } else if (error.response?.status === 404) {
    throw new Error('Resource not found.');
  } else if (error.response?.data?.detail) {
    throw new Error(error.response.data.detail);
  } else if (error.message) {
    throw new Error(error.message);
  } else {
    throw new Error(`${operation} failed. Please try again.`);
  }
};

export const agentService = {
  // ✅ OPTIMIZED: Get agents with datasource info in one call
  getAgentsWithDatasources: async () => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.get('/api/agents/with-datasources');
      console.log('✅ getAgentsWithDatasources success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Getting agents with datasources');
    }
  },

  // Get datasources that have metadata available
  getDatasourcesWithMetadata: async () => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.get('/api/agents/datasources-with-metadata');
      console.log('✅ getDatasourcesWithMetadata success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Getting datasources with metadata');
    }
  },

  // Get tables with metadata for a specific datasource
  getTablesWithMetadata: async (datasourceId) => {
    try {
      if (!datasourceId) {
        throw new Error('Datasource ID is required');
      }
      
      const api = createAuthenticatedRequest();
      const response = await api.get(`/api/agents/tables/${datasourceId}`);
      console.log(`✅ getTablesWithMetadata success for datasource ${datasourceId}:`, response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, `Getting tables for datasource ${datasourceId}`);
    }
  },

  // Create a new agent
  createAgent: async (agentData) => {
    try {
      if (!agentData || !agentData.agent_name || !agentData.datasource_id || !agentData.selected_tables?.length) {
        throw new Error('Invalid agent data. Agent name, datasource ID, and selected tables are required.');
      }
      
      console.log('🔄 Creating agent:', agentData);
      const api = createAuthenticatedRequest();
      const response = await api.post('/api/agents', agentData);
      console.log('✅ createAgent success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Creating agent');
    }
  },

  // Get all agents for the current user
  getAgents: async () => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.get('/api/agents');
      console.log('✅ getAgents success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Getting agents');
    }
  },

  // Delete an agent
  deleteAgent: async (agentId) => {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }
      
      console.log('🗑️ Deleting agent:', agentId);
      const api = createAuthenticatedRequest();
      const response = await api.delete(`/api/agents/${agentId}`);
      console.log('✅ deleteAgent success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, `Deleting agent ${agentId}`);
    }
  },

  // Execute query using specific agent
  executeAgentQuery: async (agentId, question) => {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }
      if (!question || !question.trim()) {
        throw new Error('Question is required');
      }
      
      console.log(`🔄 Executing query for agent ${agentId}:`, question);
      const api = createAuthenticatedRequest();
      const response = await api.post(`/api/agents/${agentId}/query`, {
        question: question.trim()
      });
      console.log('✅ executeAgentQuery success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, `Executing query for agent ${agentId}`);
    }
  }
};

// ✅ ADDED: Export helper functions for use in components
export const agentServiceHelpers = {
  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    return !!token;
  },
  
  // Clear authentication tokens
  clearAuth: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
  },
  
  // Get current token (for debugging)
  getCurrentToken: () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken');
  }
};
